import React from 'react';
import { spotlight } from '@mantine/spotlight';
import { Search } from 'lucide-react';

type OrganizerSearchBarProps = {
  /** Optional className for layout tweaks (header vs dashboard) */
  className?: string;
  /** Compact mode for mobile floating header */
  compact?: boolean;
  placeholder?: string;
};

/**
 * Opens Spotlight on click/focus.
 * Spotlight lists navigation actions AND searchable organizer events together.
 */
const OrganizerSearchBar: React.FC<OrganizerSearchBarProps> = ({
  className = '',
  compact = false,
  placeholder,
}) => {
  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

  const openSpotlight = () => {
    spotlight.open();
  };

  const resolvedPlaceholder =
    placeholder ||
    (compact ? 'Search...' : 'Search events, pages, actions...');

  return (
    <div
      className={`eventox-search organizer-spotlight-trigger ${className}`}
      onClick={openSpotlight}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openSpotlight();
        }
      }}
      aria-label="Open organizer search"
      style={
        compact
          ? {
              flex: '1 1 auto',
              minWidth: 0,
              maxWidth: 220,
              cursor: 'pointer',
            }
          : { cursor: 'pointer' }
      }
    >
      <Search className="eventox-search-icon" />
      <input
        type="text"
        readOnly
        placeholder={resolvedPlaceholder}
        value=""
        onFocus={(e) => {
          e.target.blur();
          openSpotlight();
        }}
        style={{ cursor: 'pointer' }}
      />
      {!compact && (
        <span
          className="organizer-spotlight-kbd"
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 10,
            fontFamily: 'ui-monospace, monospace',
            letterSpacing: '0.04em',
            padding: '2px 6px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.15)',
            color: 'rgba(255,255,255,0.45)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {isMac ? '⌘K' : 'Ctrl+K'}
        </span>
      )}
    </div>
  );
};

export default OrganizerSearchBar;
